import { FC, ReactElement, useEffect, useState } from 'react';

import { setDrawerInvisible, setDrawerOpen } from 'store/drawerSlice';
import { setActiveInteraction, setFocusOnExtent, setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useMapInteraction } from 'hooks/useMapInteraction';

import { RaaDialog } from 'raa-react';

import { undoInteraction } from 'components/map/Interactions';
import { drawDeleteVectorSource, drawVectorSource } from 'components/map/Layers';
import { projection } from 'components/map/MapConfig';

import { InteractionTypeEnum, MapModeEnum } from 'enums';

import { Feature } from 'ol';
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { Geometry, MultiPolygon } from 'ol/geom';

import classNames from 'classnames';

interface InteractionButtonProps {
  id: string;
  icon: string;
  onClick: () => void;
  disabled: boolean;
  information: string;
  active?: boolean;
}

const InteractionButton: FC<InteractionButtonProps> = ({
  id,
  icon,
  onClick,
  information,
  active,
  disabled
}): ReactElement => (
  <div className="tw-flex tw-flex-col">
    <button
      id={id}
      className={classNames(`${icon} map-toolbar-button tw-peer !tw-shadow-none`, {
        'menu-open': active,
        'tw-border-r tw-border-solid tw-border-raa-gray-3': icon === 'raa-icon-delete',
        'tw-text-raa-orange': icon === 'raa-icon-close'
      })}
      onClick={onClick}
      disabled={disabled}
      aria-label={information}
    />

    <div
      aria-hidden={true}
      className={classNames(
        'tw-invisible tw-absolute tw-top-[-3rem] tw--ml-[4rem] tw-rounded-md tw-bg-raa-gray-5 tw-px-10 tw-py-2 tw-text-raa-white peer-focus:tw-visible',
        { 'tw--ml-0': icon === 'raa-icon-edit' }
      )}
    >
      {information}
    </div>
  </div>
);

const InteractionSelect: FC = (): ReactElement => {
  const dispatch = useAppDispatch();
  const activeInteractions = useAppSelector((state: RootState) => state.map.activeInteractions);
  const totalDrawnGeometries = useAppSelector((state: RootState) => state.map.totalDrawnGeometries);
  const { unsetInteractionOptions, interactionOptions } = useMapInteraction();
  const [activeUndo, setActiveUndo] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<{
    show: boolean;
    header?: string;
    message?: string;
    proceed?: () => void;
    cancel?: () => void;
  }>({ show: false });

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.EDIT_GEOMETRY));
    dispatch(setDrawerInvisible(true));

    return () => {
      dispatch(setDrawerInvisible(false));
      dispatch(setDrawerOpen('open'));
      dispatch(setActiveInteraction(InteractionTypeEnum.NONE));
      drawVectorSource.clear();
      drawDeleteVectorSource.clear();
      drawVectorSource.un('addfeature', () => undefined);
    };
  }, [dispatch]);

  useEffect(() => {
    if (interactionOptions.geometry) {
      const features: Feature[] = new GeoJSON().readFeatures(interactionOptions.geometry);

      const polygonFeatures: Feature<Geometry>[] = features.filter(
        (feature: Feature) => feature.getGeometry() && feature.getGeometry()!.getType() === 'Polygon'
      );

      const multiPolygonsFeatures: Feature<Geometry>[] = features.filter(
        (feature: Feature) => feature.getGeometry() && feature.getGeometry()!.getType() === 'MultiPolygon'
      );

      const newPolygonsFeatures = multiPolygonsFeatures
        .map((f: Feature<Geometry>) => (f.getGeometry() as MultiPolygon).getPolygons())
        .flat()
        .map((polygon) => new Feature(polygon));

      drawVectorSource.addFeatures([...polygonFeatures, ...newPolygonsFeatures]);
      dispatch(
        setFocusOnExtent({
          type: 'fit',
          extent: drawVectorSource.getExtent()
        })
      );
    }

    dispatch(setActiveInteraction([InteractionTypeEnum.DRAW, InteractionTypeEnum.MODIFY]));
  }, [dispatch, interactionOptions]);

  useEffect(() => {
    undoInteraction.clear();
  }, [dispatch]);

  return (
    <>
      <div
        data-testid="interactionSelect"
        className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-w-full md:tw-top-5 md:tw-flex md:tw-justify-center"
      >
        <div className="tw-pointer-events-auto tw-flex tw-justify-between tw-bg-raa-white tw-px-2 tw-py-4 tw-shadow md:tw-w-96">
          <div className="tw-flex tw-border tw-border-raa-gray-3 tw-shadow">
            <InteractionButton
              id={InteractionTypeEnum.DRAW}
              icon="raa-icon-edit"
              active={activeInteractions.includes(InteractionTypeEnum.DRAW)}
              information="Rita själv"
              disabled={false}
              onClick={() =>
                dispatch(
                  activeInteractions.includes(InteractionTypeEnum.DRAW)
                    ? setActiveInteraction(InteractionTypeEnum.NONE)
                    : setActiveInteraction([InteractionTypeEnum.DRAW, InteractionTypeEnum.MODIFY])
                )
              }
            />

            <InteractionButton
              id={InteractionTypeEnum.TRANSFORM}
              icon="raa-icon-navigator"
              active={activeInteractions.includes(InteractionTypeEnum.TRANSFORM)}
              information="Omforma"
              disabled={totalDrawnGeometries === 0}
              onClick={() =>
                dispatch(
                  activeInteractions.includes(InteractionTypeEnum.TRANSFORM)
                    ? setActiveInteraction(InteractionTypeEnum.NONE)
                    : setActiveInteraction(InteractionTypeEnum.TRANSFORM)
                )
              }
            />

            <InteractionButton
              id={InteractionTypeEnum.DRAW_HOLE}
              icon="raa-icon-cut"
              active={activeInteractions.includes(InteractionTypeEnum.DRAW_HOLE)}
              information="Rita hål"
              disabled={totalDrawnGeometries === 0}
              onClick={() =>
                dispatch(
                  activeInteractions.includes(InteractionTypeEnum.DRAW_HOLE)
                    ? setActiveInteraction(InteractionTypeEnum.NONE)
                    : setActiveInteraction(InteractionTypeEnum.DRAW_HOLE)
                )
              }
            />

            <InteractionButton
              id={InteractionTypeEnum.MODIFY_DELETE}
              icon="raa-icon-delete"
              disabled={totalDrawnGeometries === 0}
              active={activeInteractions.includes(InteractionTypeEnum.MODIFY_DELETE)}
              information="Radera yta eller vertexpunkter"
              onClick={() =>
                dispatch(
                  activeInteractions.includes(InteractionTypeEnum.MODIFY_DELETE)
                    ? setActiveInteraction(InteractionTypeEnum.NONE)
                    : setActiveInteraction([InteractionTypeEnum.MODIFY_DELETE, InteractionTypeEnum.SELECT])
                )
              }
            />
          </div>

          <div className="tw-flex tw-border tw-border-raa-gray-3 tw-shadow">
            <InteractionButton
              id="undo"
              disabled={!undoInteraction.hasUndo() && !activeUndo}
              icon="raa-icon-undo"
              information="Ångra"
              onClick={() => {
                undoInteraction.undo();
                // Se till att knappen blir disabled direkt när stacken är tom
                setActiveUndo(undoInteraction.hasUndo());
              }}
            />
          </div>

          <div className="tw-flex tw-border tw-border-raa-gray-3 tw-text-raa-green tw-shadow">
            <InteractionButton
              id="done"
              icon="raa-icon-check"
              disabled={false}
              information="Klar"
              onClick={() => {
                const geoJson = new GeoJSON();
                const featureCollection: GeoJSONFeatureCollection = JSON.parse(
                  geoJson.writeFeatures(drawVectorSource.getFeatures(), {
                    dataProjection: projection,
                    featureProjection: projection
                  })
                );

                featureCollection.crs = {
                  type: 'name',
                  properties: {
                    name: projection
                  }
                };

                let proceedGuardResult;
                if (interactionOptions.onProceedGuard) {
                  proceedGuardResult = interactionOptions.onProceedGuard(featureCollection);
                }

                if (proceedGuardResult?.blocked) {
                  setShowWarning({
                    show: true,
                    header: proceedGuardResult.header,
                    message: proceedGuardResult.message,
                    cancel: () => setShowWarning({ show: false })
                  });
                } else {
                  interactionOptions.onProceed(drawVectorSource.getFeatures().length ? featureCollection : undefined);
                }
              }}
            />
          </div>

          <InteractionButton
            id="cancel"
            icon="raa-icon-close"
            disabled={false}
            information="Avbryt"
            onClick={() => {
              setShowWarning({
                show: true,
                header: 'Vill du verkligen avbryta?',
                message: 'Eventuella ändringar kommer inte att sparas.',
                proceed: () => {
                  unsetInteractionOptions();
                  dispatch(setMapMode(MapModeEnum.INVENTERING_REGISTRATION));
                },
                cancel: () => setShowWarning({ show: false })
              });
            }}
          />
        </div>
      </div>

      {showWarning.show && (
        <RaaDialog
          header={showWarning.header}
          proceed={showWarning.proceed}
          cancel={showWarning.cancel}
          proceedButtonText="Ja"
          ariaDescribedBy="dialog-description"
        >
          <p id="dialog-description" className="tw-my-4">
            {showWarning.message}
          </p>
        </RaaDialog>
      )}
    </>
  );
};

export default InteractionSelect;
